// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
/**
 * DecisionFlow — QuotaDataReview
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-02
 * Path: ui/src/components/tenant-admin/QuotaDataReview.jsx
 *
 * S473 fix: GET /api/v1/quotas did not exist — used import-logs workaround.
 * S474 OI-QUOTA-READ-ENDPOINT: real endpoint built. Switched to GET /api/v1/quotas.
 * Filters: fiscal_period_code, employee_id, business_unit_code restored.
 * Pagination: cursor-based, limit 100 default. Load-more supported.
 * Finance role: receives aggregate shape — rendered as summary table.
 * comp_admin role: receives full rows.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE } from './DataSetupFlowShell';

// S471 OI-QUOTAS-FIELD-CONTRACT-DIVERGENCE:
// quota_reporting and quota_reporting_currency_code removed from required.
// Server-generated (mirror quota_local). Customers never supply.
const QUOTA_REQUIRED_COLS = [
  'employee_id', 'fiscal_period_code', 'quota_local', 'quota_local_currency_code',
];

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function QuotaDataReview({ tier = 'essentials', onBack, onNext }) {
  const { user } = useAuth();
  const isFinance = user?.user_type === 'finance';

  // Read state
  const [quotaData, setQuotaData]           = useState([]);
  const [aggregate, setAggregate]           = useState([]);
  const [cursor, setCursor]                 = useState(null);
  const [hasMore, setHasMore]               = useState(false);
  const [loading, setLoading]               = useState(false);
  const [inlineError, setInlineError]       = useState('');
  const [blockingError, setBlockingError]   = useState('');
  const [toast, setToast]                   = useState(null);

  // Filters
  const [filterPeriod, setFilterPeriod]     = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterBU, setFilterBU]             = useState('');

  // CSV bulk-import state
  const [csvFile, setCsvFile]               = useState(null);
  const [csvDryResult, setCsvDryResult]     = useState(null);
  const [csvCommitted, setCsvCommitted]     = useState(false);
  const [csvImporting, setCsvImporting]     = useState(false);
  const [csvError, setCsvError]             = useState('');

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...(options.headers || {}) },
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed with status ${response.status}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }, []);

  // S474: real endpoint GET /api/v1/quotas with filters and cursor pagination.
  const loadQuotas = useCallback(async (opts = {}) => {
    if (!user) return;
    const { append = false, nextCursor = null } = opts;
    setLoading(true);
    if (!append) setInlineError('');
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filterPeriod.trim())   params.set('fiscal_period_code', filterPeriod.trim());
      if (filterEmployee.trim()) params.set('employee_id', filterEmployee.trim());
      if (filterBU.trim())       params.set('business_unit_code', filterBU.trim());
      if (nextCursor)            params.set('cursor', nextCursor);

      const data = await fetchJson(`${API_BASE}/quotas?${params.toString()}`);

      if (isFinance) {
        // Finance receives { aggregate: [...], total: N }
        setAggregate(data?.aggregate ?? []);
      } else {
        // comp_admin receives { data: [...], cursor: "...", total: N }
        const rows = data?.data ?? [];
        setQuotaData(prev => append ? [...prev, ...rows] : rows);
        setCursor(data?.cursor ?? null);
        setHasMore(!!data?.cursor);
      }
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch quota data.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, user, isFinance, filterPeriod, filterEmployee, filterBU]);

  useEffect(() => {
    setQuotaData([]);
    setAggregate([]);
    setCursor(null);
    setHasMore(false);
    loadQuotas();
  }, [loadQuotas]);

  // Column mapping — S470/S471 corrected targets, retained unchanged
  const ensureQuotaColumnMapping = useCallback(async () => {
    const mappings = [
      { source_column_name: 'employee_id',                   target_column_name: 'employee_id',                   is_required: false },
      { source_column_name: 'fiscal_period_code',            target_column_name: 'fiscal_period_code',            is_required: true  },
      { source_column_name: 'quota_local',                   target_column_name: 'quota_local',                   is_required: true  },
      { source_column_name: 'quota_local_currency_code',     target_column_name: 'quota_local_currency_code',     is_required: true  },
      { source_column_name: 'quota_reporting',               target_column_name: 'quota_reporting',               is_required: false },
      { source_column_name: 'quota_reporting_currency_code', target_column_name: 'quota_reporting_currency_code', is_required: false },
      { source_column_name: 'business_unit_code',            target_column_name: 'business_unit_code',            is_required: false },
      { source_column_name: 'selling_motion',                target_column_name: 'selling_motion',                is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'quota', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed quota column mapping.');
  }, []);

  const runCsvValidate = useCallback(async (file) => {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = QUOTA_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const warnings = [];
      if (!headers.includes('business_unit_code')) warnings.push('business_unit_code absent — business unit will not be set.');
      if (!headers.includes('selling_motion')) warnings.push('selling_motion absent — selling motion will not be set.');
      setCsvDryResult({ rows: lines.length - 1, warnings, errors: [] });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvImporting(false); }
  }, []);

  const runCsvCommit = useCallback(async (file) => {
    if (!file || !csvDryResult) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      await ensureQuotaColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'quota');
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json();
      if (!res.ok) { setCsvError(json?.message || json?.detail || `Error ${res.status}`); return; }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      showToast(`${csvDryResult.rows} quota row(s) loaded.`);
      // Reload quota data from real endpoint after successful import
      setQuotaData([]);
      setCursor(null);
      setHasMore(false);
      await loadQuotas();
    } catch (e) { setCsvError(e.message || 'Commit failed.'); }
    finally { setCsvImporting(false); }
  }, [csvDryResult, ensureQuotaColumnMapping, loadQuotas, showToast]);

  const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
  const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Quota Data Review</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication is required to review quota data.
        </div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Quota Data Review</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {blockingError}
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">SETUP-02</p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Quota Data Review</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Review imported quotas by position and fiscal period. Quotas are import-fed and cannot be manually created here.
          </p>
        </div>
      </header>

      {/* Toast */}
      {toast ? (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      {/* Inline error */}
      {inlineError ? (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {inlineError}
        </div>
      ) : null}

      {/* CSV Bulk Import — comp_admin only */}
      {!isFinance ? (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-5 shadow-sm">
          <div>
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Upload <strong>Quotas.csv</strong> to set rep quotas in one pass. Validates before committing — review results before confirming.
            </p>
          </div>

          {csvError ? (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {csvError}
            </div>
          ) : null}

          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">1</span>
              <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                Quotas.csv — quota rows
                {csvCommitted ? <span className="ml-2 text-[rgb(var(--color-success))] font-normal">✓ Loaded</span> : null}
              </span>
            </div>

            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Required columns: <code>employee_id, fiscal_period_code, quota_local, quota_local_currency_code</code>
              <span className="block mt-1">Optional: <code>business_unit_code, selling_motion</code> — <code>quota_reporting</code> and <code>quota_reporting_currency_code</code> are derived server-side from <code>quota_local</code>.</span>
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                accept=".csv"
                className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setCsvFile(f);
                  setCsvDryResult(null);
                  setCsvCommitted(false);
                  setCsvError('');
                }}
              />
              <button
                type="button"
                className={btnSecondary}
                disabled={!csvFile || csvImporting}
                onClick={() => runCsvValidate(csvFile)}
              >
                {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
              </button>
            </div>

            {csvDryResult ? (
              <div className="mt-1 flex flex-col gap-2">
                <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Rows parsed: <strong>{csvDryResult.rows}</strong> · Quotas to load: <strong>{csvDryResult.rows}</strong>
                </div>
                {csvDryResult.warnings?.map((w, i) => (
                  <p key={i} className="text-tok-xs text-amber-600">⚠ {w}</p>
                ))}
                {csvDryResult.errors?.length > 0 ? (
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-tok-sm text-[rgb(var(--color-success))]">
                      ✓ Validation passed — {csvDryResult.rows} quota(s) ready to load.
                    </span>
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={csvImporting}
                      onClick={() => runCsvCommit(csvFile)}
                    >
                      {csvImporting ? 'Loading…' : 'Confirm and load'}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Quota data — filters + table */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
          {isFinance ? 'Quota summary (aggregate)' : 'Quota records'}
        </h3>

        {/* Filters — comp_admin only; finance sees all periods in aggregate */}
        {!isFinance ? (
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Fiscal period</label>
              <input
                type="text"
                placeholder="e.g. FY2024-Q1"
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-36"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Employee ID</label>
              <input
                type="text"
                placeholder="e.g. EMP-001"
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-32"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Business unit</label>
              <input
                type="text"
                placeholder="e.g. EMEA"
                value={filterBU}
                onChange={(e) => setFilterBU(e.target.value)}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-28"
              />
            </div>
            <button type="button" className={btnSecondary} onClick={() => loadQuotas()} disabled={loading}>
              {loading ? 'Loading…' : 'Apply filters'}
            </button>
            <button
              type="button"
              className={btnSecondary}
              disabled={loading}
              onClick={() => { setFilterPeriod(''); setFilterEmployee(''); setFilterBU(''); }}
            >
              Clear
            </button>
          </div>
        ) : null}

        {loading && !quotaData.length && !aggregate.length
          ? <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
          : null}

        {/* Finance aggregate table */}
        {isFinance ? (
          aggregate.length === 0 && !loading ? (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No quota data found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Period</th>
                    <th className={thClass}>Business unit</th>
                    <th className={thClass}>Count</th>
                    <th className={thClass}>Total quota (reporting)</th>
                    <th className={thClass}>Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregate.map((row, i) => (
                    <tr key={i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass}>{row.fiscal_period_code || '—'}</td>
                      <td className={tdClass}>{row.business_unit_code || '—'}</td>
                      <td className={tdClass}>{row.quota_count ?? '—'}</td>
                      <td className={tdClass}>{row.total_quota_reporting ?? '—'}</td>
                      <td className={tdClass}>{row.quota_reporting_currency_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          /* comp_admin full rows */
          quotaData.length === 0 && !loading ? (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No quota records found. Upload a CSV above to begin.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Employee ID</th>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Period</th>
                    <th className={thClass}>Business unit</th>
                    <th className={thClass}>Quota (local)</th>
                    <th className={thClass}>Currency</th>
                    <th className={thClass}>Effective quota</th>
                    <th className={thClass}>Selling motion</th>
                  </tr>
                </thead>
                <tbody>
                  {quotaData.map((row) => (
                    <tr key={row.quota_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass}>{row.employee_id || '—'}</td>
                      <td className={tdClass}>{row.full_name || '—'}</td>
                      <td className={tdClass}>{row.fiscal_period_code || '—'}</td>
                      <td className={tdClass}>{row.business_unit_code || '—'}</td>
                      <td className={tdClass}>{row.quota_local ?? '—'}</td>
                      <td className={tdClass}>{row.quota_local_currency_code || '—'}</td>
                      <td className={tdClass}>{row.effective_quota ?? '—'}</td>
                      <td className={tdClass}>{row.selling_motion_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        <div className="flex items-center gap-2">
          <button type="button" className={btnSecondary} onClick={() => loadQuotas()} disabled={loading}>
            Refresh
          </button>
          {hasMore ? (
            <button
              type="button"
              className={btnSecondary}
              disabled={loading}
              onClick={() => loadQuotas({ append: true, nextCursor: cursor })}
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          ) : null}
          {quotaData.length > 0 ? (
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{quotaData.length} record(s) loaded{hasMore ? ' — more available' : ''}</span>
          ) : null}
        </div>
      </div>

      {/* Footer nav */}
      <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" className={btnSecondary} onClick={onBack}>Back</button>
        <button type="button" className={btnPrimary} onClick={onNext}>Continue to ramp schedules</button>
      </footer>
    </section>
  );
}

export default QuotaDataReview;
